import {
	useCreateDocumentDraftMutation,
	ModifyInvoiceItemInput,
	DocumentType,
} from "@repo/server-api";
import {
	ResultFromData,
	ResultFromError,
	ResultFromException,
	useNotificationGraphql,
} from "@repo/shared/common";
import { useTranslation } from "react-i18next";

import type { Invoice } from "../types/CommercialDocumentTypes";
import type { GraphqlQueryResult } from "@repo/shared/common";
import { InvoiceItem } from "../types";

type UseCreateInvoiceOptions = {
	onSaved: (id: string) => void;
};

type UseCreateNewInvoiceResult = {
	save: (invoice: Invoice) => void;
	loading: boolean;
};

// Here is the hook
export function useCreateInvoiceHook(options: UseCreateInvoiceOptions): UseCreateNewInvoiceResult {
	const { t } = useTranslation();

	const [createInvoiceMutation] = useCreateDocumentDraftMutation();

	const createInvoice = async (invoice: Invoice): Promise<GraphqlQueryResult<string>> => {
		const createInvoice = await createInvoiceMutation({
			variables: {
				documentType: DocumentType.Invoice,
				clientContactPersonId: invoice.contactPerson?.id || null,
				clientId: invoice.client?.id || null,
				concludingText: invoice.footerNote || null,
				contractorId: invoice.contractor?.id || null,
				introductoryText: invoice.headerNote || null,
				items: invoice.items.map((item: InvoiceItem, index: number) => ({
					description: item.description,
					discountPercent: item.discount ?? 0,
					name: item.name,
					quantity: item.quantity,
					tax: item.tax,
					taxCode: item.taxCode,
					unit: item.unit,
					unitPriceNet: item.unitPrice,
					positionNo: index,
				})) as ModifyInvoiceItemInput[],
				notes: invoice.notes || null,
				paymentDeadlineInDays: invoice.paymentDeadlineDays,
				projectId: invoice.project?.id || null,
				isProforma: invoice.isProforma || false,
			},
		});
		if (
			createInvoice.data?.createDocumentDraft.errors &&
			createInvoice.data?.createDocumentDraft.errors.length > 0
		) {
			return ResultFromError<string>(createInvoice.data.createDocumentDraft.errors);
		}
		if (createInvoice.errors) return ResultFromException<string>(createInvoice.errors);

		const invoiceId = createInvoice.data?.createDocumentDraft.commercialDocument?.id;
		if (!invoiceId) return ResultFromException<string>(t("no-document-id-returned"));

		return ResultFromData<string>(invoiceId);
	};

	const { save, loading } = useNotificationGraphql<Invoice, string>({
		onComplete: (id) => {
			options.onSaved(id);
		},

		graphqlCall: createInvoice,

		successMessage: t("invoice-created-successfully"),
	});

	return {
		save,
		loading,
	};
}
